import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import styles from './PFPBuilder.module.css';

interface PFPBuilderProps {
  signerUri: string;
  contractAddr: string;
  layers: any;
  steps: any;
  provider?: any;
  chainId?: number;
  address?: string;
  ipfsUrl?: string;
  mintBlockExplorerUrl?: string;
  mintBlockExplorerTxnPath?: string;
  layerCacheId?: string;
  resourceBaseUri?: string;
}

function PFPBuilder({
  signerUri,
  contractAddr,
  layers,
  steps,
  provider,
  chainId,
  address,
  ipfsUrl = 'https://ipfs.io/ipfs/{hash}',
  mintBlockExplorerUrl = '',
  mintBlockExplorerTxnPath = '/tx/',
  layerCacheId = 'pfp-builder-layer-choices',
  resourceBaseUri = '/pfp_builder_resources',
}: PFPBuilderProps) {
  /* ABI */
  /* ---------------------------------------------------------------------- */

  const contractAbi = [
    {
      inputs: [
        {
          internalType: 'address',
          name: 'to',
          type: 'address',
        },
        {
          internalType: 'bytes32',
          name: '_tokenURI',
          type: 'bytes32',
        },
        {
          internalType: 'bytes32',
          name: '_tokenAttribute',
          type: 'bytes32',
        },
        {
          internalType: 'bytes',
          name: 'signature',
          type: 'bytes',
        },
      ],
      name: 'safeMint',
      outputs: [],
      stateMutability: 'nonpayable',
      type: 'function',
    },
    {
      type: 'function',
      stateMutability: 'view',
      outputs: [{ type: 'uint256', name: '', internalType: 'uint256' }],
      name: 'balanceOf',
      inputs: [{ type: 'address', name: 'owner', internalType: 'address' }],
    },
  ];

  /* Ref */
  /* ---------------------------------------------------------------------- */

  const firstUpdate = useRef(true);

  /* State */
  /* ---------------------------------------------------------------------- */

  const [step, setStep] = useState(layers[0].name);

  const [layerChoices, setLayerChoices] = useState({});

  const [mintApprovalData, setMintApprovalData] = useState({
    imageIpfsHash: '',
    ipfsHash: '',
    traits: '',
    signature: '',
  });
  const [mintApprovalProcessing, setMintApprovalProcessing] = useState(false);
  const [mintApprovalError, setMintApprovalError] = useState('');

  const [mintProcessing, setMintProcessing] = useState(false);
  const [mintError, setMintError] = useState('');
  const [isMintTxnProcessing, setIsMintTxnProcessing] = useState(false);
  const [mintTxnId, setMintTxnId] = useState(null);

  const [isMintComplete, setIsMintComplete] = useState(false);

  const [tokenBalance, setTokenBalance] = useState(0);

  const [mintModalOpen, setMintModalOpen] = useState(false);
  const [mintApprovalErrorModalOpen, setMintApprovalErrorModalOpen] = useState(false);

  /* Init */
  /* ---------------------------------------------------------------------- */

  function init() {
    restoreAllLayerCache();
    queryTokenBalance();
  }

  /* Chain */
  /* ---------------------------------------------------------------------- */

  function getMintBlockExplorerUrl() {
    if (mintBlockExplorerUrl !== '') {
      return mintBlockExplorerUrl;
    }

    switch (chainId) {
      case 3:
        return 'https://ropsten.etherscan.io';
      case 4:
        return 'https://rinkeby.etherscan.io';
      case 5:
        return 'https://goerli.etherscan.io';
      case 42:
        return 'https://kovan.etherscan.io';
      default:
        return 'https://etherscan.io';
    }
  }

  /* Layer Cache */
  /* ---------------------------------------------------------------------- */

  function setLayerCache(layerChoices) {
    localStorage.setItem(layerCacheId, JSON.stringify(layerChoices));
  }

  function getLayerCache(): string {
    return JSON.parse(localStorage.getItem(layerCacheId));
  }

  function restoreAllLayerCache() {
    const layerChoices = {};

    layers.forEach((layer) => {
      layerChoices[layer.name] = layer.default;
    });

    const layerChoicesCache = getLayerCache();

    if (layerChoicesCache !== null && typeof layerChoicesCache === 'object') {
      Object.keys(layerChoicesCache).forEach((key) => {
        if (key in layerChoices) {
          layerChoices[key] = layerChoicesCache[key];
        }
      });
    }

    setLayerChoices(layerChoices);
  }

  /* Layers */
  /* ---------------------------------------------------------------------- */

  function setLayer(name: string, optionName: string) {
    saveLayerChoices({
      ...layerChoices,
      [name]: optionName,
    });
  }

  function getLayer(name: string): string {
    return typeof layerChoices[name] === 'string' ? layerChoices[name] : '';
  }

  function saveLayerChoices(layerChoices) {
    setLayerCache(layerChoices);
    setLayerChoices(layerChoices);
  }

  function getLayerProperties(name: string) {
    return layers
      .filter((layer) => {
        return layer.name === name;
      })
      .pop();
  }

  function getLayerOptionProperties(name: string, optionName: string) {
    return getLayerProperties(name)
      .options.filter((option) => {
        return option.name === optionName;
      })
      .pop();
  }

  function getRandomLayerOptionIndex(layer): number {
    return Math.floor(Math.random() * layer.options.length);
  }

  function getRandomLayerOption(layer): string {
    return layer.options[getRandomLayerOptionIndex(layer)].name;
  }

  function resetLayers() {
    const layerChoices = {};

    layers.forEach((layer) => {
      layerChoices[layer.name] = layer.default;
    });

    saveLayerChoices(layerChoices);
  }

  function randomLayers() {
    const layerChoices = {};

    layers.forEach((layer) => {
      layerChoices[layer.name] = getRandomLayerOption(layer);
    });

    saveLayerChoices(layerChoices);
  }

  /* Minting Conditions */
  /* ---------------------------------------------------------------------- */

  function hasAllRequiredLayers(): boolean {
    return (
      layers.filter((layer) => {
        console.log(layer);
        console.log(getLayer(layer.name));
        return getLayer(layer.name) === '';
      }).length === 0
    );
  }

  function hasAllRequiredApprovals(): boolean {
    return (
      Object.entries(mintApprovalData).filter(([key, value]) => {
        console.log(key);
        console.log(value);
        return value === '';
      }).length === 0
    );
  }

  function canApproveMint(): boolean {
    return mintApprovalProcessing === false && mintProcessing === false && hasAllRequiredLayers();
  }

  function canMint(): boolean {
    return mintProcessing === false && canApproveMint() && hasAllRequiredApprovals();
  }

  /* Interface Conditions */
  /* ---------------------------------------------------------------------- */

  function isThumbSelected(name: string, optionName: string): boolean {
    return getLayer(name) === optionName;
  }

  function isStepActive(stepToCheck: string): boolean {
    return step === stepToCheck;
  }

  /* CSS Classes */
  /* ---------------------------------------------------------------------- */

  function getThumbSelectedStatus(name: string, optionName: string) {
    return isThumbSelected(name, optionName) === true ? 'active' : 'inactive';
  }

  function getStepStatus(step: string): string {
    return isStepActive(step) === true ? 'active' : 'inactive';
  }

  /* Actions */
  /* ---------------------------------------------------------------------- */

  function stepClick(name: string) {
    setStep(name);
  }

  function resetClick() {
    resetLayers();
  }

  function randomClick() {
    randomLayers();
  }

  async function mintApprovalClick() {
    if (canApproveMint() === true) {
      setIsMintComplete(false);
      await mintApproval();
    }
  }

  async function mintClick() {
    if (canMint() === true) {
      await mint();
    }
  }

  /* Mint Approval Modal */
  /* ---------------------------------------------------------------------- */

  function closeModal() {
    setMintModalOpen(false);
  }

  function openMintModal() {
    setMintModalOpen(true);
  }

  function closeMintApprovalErrorModal() {
    setMintApprovalErrorModalOpen(false);
  }

  function openMintApprovalErrorModal() {
    setMintApprovalErrorModalOpen(true);
  }

  function getMintApprovalImgUrl() {
    return ipfsUrl.replace('{hash}', mintApprovalData.imageIpfsHash);
  }

  /* Mint */
  /* ---------------------------------------------------------------------- */

  async function getMintApproval(layerChoices) {
    const mintParams = {
      traits: layerChoices,
    };

    const request = new Request(signerUri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(mintParams),
    });

    const response = await fetch(request);
    console.log(response);

    const body = await response.json();
    console.log(body);

    if (response.ok === false) {
      throw new Error(body.detail.message);
    }

    return {
      imageIpfsHash: body.data.published.image_ipfs_hash,
      ipfsHash: body.data.published.metadata_ipfs_hash_base16_bytes32,
      traits: body.data.traits_hex,
      signature: body.data.signature,
    };
  }

  async function getContract() {
    return new ethers.Contract(contractAddr, contractAbi, provider.getSigner());
  }

  async function mintTxn(address, ipfsHash, traits, signature) {
    const contract = await getContract();

    return await contract.safeMint(address, ipfsHash, traits, signature);
  }

  function timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function mintApproval() {
    try {
      setMintApprovalProcessing(true);

      // await timeout(3000);
      // await onMintApprovalComplete({}, 'Lorem ipsum dolor sit amet, consectetur adipiscing elit');
      // return;

      console.log(layerChoices);

      const mintApprovalData = await getMintApproval(layerChoices);
      // const mintApprovalData = {
      //   imageIpfsHash: 'bafybeidjf2v2eqksmnnvmhskwponnek2gqzr7w5uhrr7r6y6ep334swfoe',
      //   ipfsHash: '0xfd5cd9a14e67671ceccc454a48d001366c3f8c92693eb847782c51251802a649',
      //   traits: '0x00000000000000000000000000000000000000000000000000080f030002270e',
      //   signature:
      //     '0x7fb48737740c212b60608bd6d3a88e5d2b5e4918888dd3815d4d5d36a3b3ea910e6dfb2678464a70e241afcb55523fe46b7779a3c1fa34611db879b6a2887d0a1c',
      // };
      console.log(mintApprovalData);

      await onMintApprovalComplete(mintApprovalData, '');
    } catch (e) {
      console.error(e);
      console.log(e);

      await onMintApprovalComplete({}, getDecodeErrorMessage(e));
    }
  }

  async function mint() {
    try {
      setMintProcessing(true);

      // await timeout(3000);
      // return;

      const tx = await mintTxn(
        address,
        mintApprovalData.ipfsHash,
        mintApprovalData.traits,
        mintApprovalData.signature,
      );

      setIsMintTxnProcessing(true);
      setMintTxnId(tx.hash);

      // wait for the transaction to be mined
      const receipt = await tx.wait();

      if (!receipt.blockNumber || Number(receipt.status) !== 1) {
        throw new Error('Transaction failed. Please try again.');
      }

      await onMintComplete(true, '');
    } catch (e) {
      console.error(e);
      console.log(e);

      await onMintComplete(false, getDecodeErrorMessage(e));
    }
  }

  async function onMintApprovalComplete(mintApprovalData, errorMessage) {
    setMintApprovalData(mintApprovalData);

    setMintApprovalError(errorMessage);
    setMintApprovalProcessing(false);

    if (errorMessage) {
      openMintApprovalErrorModal();
    } else {
      openMintModal();
    }
  }

  async function onMintComplete(mintComplete, errorMessage) {
    await refreshTokenBalance();

    setIsMintComplete(mintComplete);

    setMintError(errorMessage);
    setIsMintTxnProcessing(false);
    setMintTxnId(null);
    setMintProcessing(false);
  }

  async function refreshTokenBalance() {
    try {
      const balance = await queryTokenBalance();

      setTokenBalance(balance);
    } catch (e) {
      console.error(e);
      console.log(e);

      setTokenBalance(0);
    }
  }

  async function queryTokenBalance() {
    try {
      console.log('queryTokenBalance');

      const contract = await getContract();

      const balance = await contract.balanceOf(address);

      console.log(balance);

      return balance.toNumber();
    } catch (e) {
      console.error(e);
      console.log(e);

      return 0;
    }
  }

  function getErrorMessage(error) {
    if (
      typeof error.data !== 'undefined' &&
      typeof error.data.message !== 'undefined' &&
      error.data.message.startsWith('Error: VM Exception ')
    ) {
      return error.data.message;
    }

    if (
      typeof error.data !== 'undefined' &&
      typeof error.data.message !== 'undefined' &&
      error.data.message.startsWith('Reverted ')
    ) {
      return error.data.message;
    }

    if (typeof error.message !== 'undefined') {
      return error.message;
    }

    return error;
  }

  function decodeErrorMessage(errorMessage) {
    if (errorMessage.startsWith('Reverted ')) {
      const errorCode = errorMessage.slice(9);

      const decodedMessage = ethers.utils.toUtf8String(errorCode);

      return decodedMessage;
    }

    return errorMessage;
  }

  function getDecodeErrorMessage(error) {
    return decodeErrorMessage(getErrorMessage(error));
  }

  /* Components */
  /* ---------------------------------------------------------------------- */

  function Preview() {
    return (
      <div className={styles['pfp-builder-preview']}>
        <img
          className={styles['pfp-builder-preview__placeholder']}
          src={`${resourceBaseUri}/preview/placeholder.png`}
          alt="Placeholder"
        ></img>
        {layers.map((layer, index) => (
          <PreviewLayer name={layer.name} key={index}></PreviewLayer>
        ))}
      </div>
    );
  }

  function StepNav() {
    return (
      <ul className={styles['pfp-builder-controls-nav']}>
        {steps.map((step, index) => (
          <StepButton step={step.name} name={step.display} key={index}></StepButton>
        ))}
      </ul>
    );
  }

  function Steps() {
    return (
      <div className={styles['pfp-builder-controls-steps']}>
        {steps.map((step, index) => (
          <Step step={step.name} key={index}>
            {step.layers.map((stepLayer, index2) => (
              <StepThumbnails name={stepLayer.display} key={index2}>
                <Thumbnails name={stepLayer.name}></Thumbnails>
              </StepThumbnails>
            ))}
          </Step>
        ))}
      </div>
    );
  }

  function PreviewLayer(props: { name: string }) {
    if (!getLayer(props.name)) {
      return <div className={styles['pfp-builder-preview__layer']}></div>;
    }

    const layer = getLayerProperties(props.name);
    const layerOption = getLayerOptionProperties(props.name, getLayer(props.name));

    return (
      <img
        className={styles['pfp-builder-preview__layer']}
        src={`${resourceBaseUri}/preview/${layer.name}/${('000' + layerOption.id).slice(-3)}-${
          layerOption.name
        }.${layerOption.ext}`}
        alt={`Layer: ${layerOption.name}`}
      ></img>
    );
  }

  function StepButton(props: { step: string; name: string }) {
    return (
      <li
        className={`
          ${styles['pfp-builder-controls-nav__entry']}
          ${styles[`pfp-builder-controls-nav__entry--${getStepStatus(props.step)}`]}
        `}
      >
        <button
          className={styles['pfp-builder-controls-nav__entry-button']}
          onClick={() => stepClick(props.step)}
        >
          {props.name}
        </button>
      </li>
    );
  }

  function Step(props: { step: string; children: any }) {
    return (
      <section
        className={`
          ${styles['pfp-builder-controls-step']}
          ${styles[`pfp-builder-controls-step--${getStepStatus(props.step)}`]}
        `}
      >
        {props.children}
      </section>
    );
  }

  function StepThumbnails(props: { name: string; children: any }) {
    return (
      <section className={styles['pfp-builder-controls-step-thumbnails']}>
        {props.name && (
          <h5 className={styles['pfp-builder-controls-step-thumbnails__subheader']}>
            {props.name}
          </h5>
        )}
        <div className={styles['pfp-builder-controls-step-thumbnails__thumbnails']}>
          {props.children}
        </div>
      </section>
    );
  }

  function Thumbnails(props: { name: string }) {
    const layer = getLayerProperties(props.name);

    return (
      <ul className={styles['pfp-builder-thumbnails']}>
        {layer.options.map((layerOption, index) => (
          <li
            className={`
              ${styles['pfp-builder-thumbnails__thumbnail']}
              ${
                styles[
                  `pfp-builder-thumbnails__thumbnail--${getThumbSelectedStatus(
                    layer.name,
                    layerOption.name,
                  )}`
                ]
              }
            `}
            key={index}
          >
            <img
              loading="eager"
              className={styles['pfp-builder-thumbnails__thumbnail-placeholder']}
              src={`${resourceBaseUri}/thumb/placeholder.png`}
              alt={`${layer.name}: placeholder`}
            ></img>
            <img
              loading="lazy"
              className={styles['pfp-builder-thumbnails__thumbnail-img']}
              src={`${resourceBaseUri}/thumb/${layer.name}/${('000' + layerOption.id).slice(-3)}-${
                layerOption.name
              }.${layerOption.thumbExt}`}
              alt={`${layer.name}: ${layerOption.name}`}
              onClick={() => setLayer(layer.name, layerOption.name)}
            ></img>
          </li>
        ))}
      </ul>
    );
  }

  /* Bootstrap */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (firstUpdate.current === false) {
      return;
    }

    if (firstUpdate.current) {
      firstUpdate.current = false;
    }

    // Init
    init();
  });

  /* Main Component */
  /* ---------------------------------------------------------------------- */

  return (
    <div className={styles['pfp-builder']}>
      <div className={styles['pfp-builder__preview']}>
        <Preview></Preview>
      </div>
      <div className={styles['pfp-builder__controls']}>
        <div className={styles['pfp-builder-controls']}>
          <header className={styles['pfp-builder-controls__header']}>
            <button
              className={`
                ${styles['pfp-builder-controls__header-button']}
                ${styles['pfp-builder-controls__header-button--reset']}
              `}
              onClick={() => resetClick()}
            >
              Reset
            </button>
            <button
              className={`
                ${styles['pfp-builder-controls__header-button']}
                ${styles['pfp-builder-controls__header-button--random']}
              `}
              onClick={() => randomClick()}
            >
              Random
            </button>
            <button
              className={`
                ${styles['pfp-builder-controls__header-button']}
                ${styles['pfp-builder-controls__header-button--mint']}
              `}
              onClick={() => mintApprovalClick()}
              disabled={!canApproveMint()}
            >
              {mintApprovalProcessing === false && <span>★ Mint</span>}
              {mintApprovalProcessing === true && (
                <div>
                  <div className={styles['pfp-builder-controls__header-button-loading-icon']}>
                    <div className={styles['pfp-builder__loading-icon']}>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                </div>
              )}
            </button>
          </header>
          <main className={styles['pfp-builder-controls__main']}>
            <nav className={styles['pfp-builder-controls__nav']}>
              <StepNav></StepNav>
            </nav>
            <div className={styles['pfp-builder-controls__steps']}>
              <Steps></Steps>
            </div>
          </main>
        </div>
      </div>
      <div className="pfp-builder__end"></div>

      {mintApprovalErrorModalOpen && (
        <div className={styles['pfp-builder__modal']}>
          <div className={styles['pfp-builder__modal-inner']}>
            <button
              className={styles['pfp-builder__modal-close']}
              onClick={() => closeMintApprovalErrorModal()}
            >
              <span className={styles['pfp-builder__modal-close-icon']}></span>
            </button>

            <div>
              <div className={styles['pfp-builder__modal-body']}>
                <h2 className={styles['pfp-builder__mint-confirmation-header']}>
                  Sorry there was an issue.
                </h2>
                <div
                  className={`
                    ${styles['pfp-builder-response']}
                    ${styles['pfp-builder-response--error']}
                  `}
                >
                  <div className={styles['pfp-builder-response__message']}>{mintApprovalError}</div>
                </div>
              </div>

              <div className={styles['pfp-builder__modal-actions']}>
                <button
                  className={styles['pfp-builder__modal-button']}
                  onClick={() => closeMintApprovalErrorModal()}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mintModalOpen && (
        <div className={styles['pfp-builder__modal']}>
          <div className={styles['pfp-builder__modal-inner']}>
            <button className={styles['pfp-builder__modal-close']} onClick={() => closeModal()}>
              <span className={styles['pfp-builder__modal-close-icon']}></span>
            </button>

            {!isMintComplete && (
              <div>
                <div className={styles['pfp-builder__modal-body']}>
                  <div className={styles['pfp-builder__mint-confirmation-description']}>
                    <h2 className={styles['pfp-builder__mint-confirmation-header']}>
                      One more step.
                    </h2>
                    <p>
                      Please verify the image below is what you want. Then click the "Confirm &amp;
                      Mint" button below to mint your NFT with this image.
                    </p>
                  </div>
                  <div className={styles['pfp-builder__mint-confirmation-image']}>
                    <div className={styles['pfp-builder__mint-confirmation-loading-icon']}>
                      <div className={styles['pfp-builder__loading-icon']}>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                    </div>
                    <img
                      className={styles['pfp-builder__mint-confirmation-image-placeholder']}
                      src={`${resourceBaseUri}/preview/placeholder.png`}
                      alt="Placeholder"
                    ></img>
                    <img
                      className={styles['pfp-builder__mint-confirmation-image-final']}
                      src={getMintApprovalImgUrl()}
                      alt="Confirmation image"
                    ></img>
                  </div>
                </div>

                <div className={styles['pfp-builder__modal-actions']}>
                  {isMintTxnProcessing && (
                    <div className={styles['pfp-builder-response']}>
                      <div className={styles['pfp-builder-response__loading-icon']}>
                        <div className={styles['pfp-builder__loading-icon']}>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                      </div>
                      <div className={styles['pfp-builder-response__message']}>
                        <div>Transaction is being processed...</div>
                        <div>
                          <a
                            className={styles['pfp-builder-response__link']}
                            href={`${getMintBlockExplorerUrl()}${mintBlockExplorerTxnPath}${mintTxnId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View Transaction
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  {mintError && (
                    <div
                      className={`
                        ${styles['pfp-builder-response']}
                        ${styles['pfp-builder-response--error']}
                      `}
                    >
                      <div className={styles['pfp-builder-response__message']}>{mintError}</div>
                    </div>
                  )}

                  {mintProcessing === false && (
                    <button
                      className={styles['pfp-builder__modal-button']}
                      onClick={() => mintClick()}
                    >
                      <span>Confirm &amp; Mint</span>
                    </button>
                  )}
                  {mintProcessing === true && (
                    <div>
                      <div className={styles['pfp-builder__modal-button-loading-icon']}>
                        <div className={styles['pfp-builder__loading-icon']}>
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isMintComplete && (
              <div>
                <div className={styles['pfp-builder__modal-body']}>
                  <div className={styles['pfp-builder__mint-confirmation-description']}>
                    <h2 className={styles['pfp-builder__mint-confirmation-header']}>Congrats!</h2>
                    <p>Your NFT has been minted.</p>
                  </div>
                  <div className={styles['pfp-builder__mint-confirmation-image']}>
                    <img src={getMintApprovalImgUrl()} alt="Confirmation image"></img>
                  </div>
                </div>

                <div className={styles['pfp-builder__modal-actions']}>
                  <button
                    className={styles['pfp-builder__modal-button']}
                    onClick={() => closeModal()}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PFPBuilder;
